import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Paper,
    CircularProgress,
    Tooltip,
    Fade,
    Grow,
    Slide,
    LinearProgress,
    Badge,
} from '@mui/material'
import {
    Send,
    Chat as ChatIcon,
    AttachFile,
    Image as ImageIcon,
    Mic,
    Stop,
    InsertDriveFile,
    PlayArrow,
    Pause,
    FiberManualRecord,
    KeyboardArrowDown,
    VolumeUp,
    VolumeOff,
} from '@mui/icons-material'
import { chatPublicApi } from '@api/chat'

const POLL_INTERVAL = 5000
const API_URL = import.meta.env.VITE_API_URL || '/api'
const FILE_BASE = API_URL.replace(/\/api\/?$/, '/api')

// ── Dynamic Brand Colors (from config) ──
const DEFAULT_PRIMARY = '#1a3a6b'
const DEFAULT_SECONDARY = '#1a5c4a'

const getBrand = (config) => {
    const p = config?.primary_color || DEFAULT_PRIMARY
    const s = config?.secondary_color || DEFAULT_SECONDARY
    return {
        primary: p,
        primaryDark: p,
        primaryLight: p + '66',
        secondary: s,
        gradient: `linear-gradient(135deg, ${p} 0%, ${s} 100%)`,
        gradientLight: `linear-gradient(135deg, ${p}22 0%, ${s}10 50%, #f8f9fa 100%)`,
        headerGradient: `linear-gradient(135deg, ${p} 0%, ${p} 50%, ${s} 100%)`,
    }
}

// Fallback BRAND for components before config loads
const BRAND = getBrand(null)

/**
 * Visitor UUID
 */
const getVisitorId = () => {
    let id = localStorage.getItem('chat_visitor_id')
    if (!id) {
        id = crypto.randomUUID ? crypto.randomUUID() : `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        localStorage.setItem('chat_visitor_id', id)
    }
    return id
}

// ────────────────────────────────────────────────
//  Notification Sound (Web Audio API – no files needed)
// ────────────────────────────────────────────────
const playNotificationSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.type = 'sine'
        // Two-tone chime
        osc.frequency.setValueAtTime(880, ctx.currentTime)
        osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.12)
        gain.gain.setValueAtTime(0.15, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35)
        osc.start(ctx.currentTime)
        osc.stop(ctx.currentTime + 0.4)
    } catch { /* audio not available */ }
}

// ────────────────────────────────────────────────
//  Custom Audio Player Component
// ────────────────────────────────────────────────
const CustomAudioPlayer = ({ src, isVisitor }) => {
    const audioRef = useRef(null)
    const [playing, setPlaying] = useState(false)
    const [progress, setProgress] = useState(0)
    const [duration, setDuration] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const animFrame = useRef(null)

    const fmtTime = (s) => {
        if (!s || isNaN(s)) return '0:00'
        const m = Math.floor(s / 60)
        const sec = Math.floor(s % 60)
        return `${m}:${sec.toString().padStart(2, '0')}`
    }

    const updateProgress = useCallback(() => {
        if (audioRef.current) {
            const ct = audioRef.current.currentTime
            const dur = audioRef.current.duration || 1
            setCurrentTime(ct)
            setProgress((ct / dur) * 100)
        }
        if (playing) animFrame.current = requestAnimationFrame(updateProgress)
    }, [playing])

    useEffect(() => {
        if (playing) {
            animFrame.current = requestAnimationFrame(updateProgress)
        }
        return () => {
            if (animFrame.current) cancelAnimationFrame(animFrame.current)
        }
    }, [playing, updateProgress])

    const togglePlay = () => {
        if (!audioRef.current) return
        if (playing) {
            audioRef.current.pause()
            setPlaying(false)
        } else {
            audioRef.current.play()
            setPlaying(true)
        }
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
                onEnded={() => { setPlaying(false); setProgress(0); setCurrentTime(0) }}
            />
            <IconButton size="small" onClick={togglePlay}
                sx={{ color: isVisitor ? '#fff' : BRAND.primary, p: 0.5 }}>
                {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>
            <Box sx={{ flex: 1 }}>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        height: 3, borderRadius: 2,
                        bgcolor: isVisitor ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.08)',
                        '& .MuiLinearProgress-bar': {
                            bgcolor: isVisitor ? '#fff' : BRAND.primary,
                            borderRadius: 2,
                        },
                    }}
                />
            </Box>
            <Typography variant="caption" sx={{
                color: isVisitor ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                fontSize: 10, minWidth: 32,
            }}>
                {playing ? fmtTime(currentTime) : fmtTime(duration)}
            </Typography>
        </Box>
    )
}

// ────────────────────────────────────────────────
//  Voice Recorder Component
// ────────────────────────────────────────────────
const VoiceRecorder = ({ onRecordingComplete, onCancel }) => {
    const [recording, setRecording] = useState(false)
    const [recordingTime, setRecordingTime] = useState(0)
    const mediaRecorderRef = useRef(null)
    const chunksRef = useRef([])
    const timerRef = useRef(null)

    useEffect(() => {
        startRecording()
        return () => { if (timerRef.current) clearInterval(timerRef.current) }
    }, [])

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' })
            mediaRecorderRef.current = mediaRecorder
            chunksRef.current = []
            mediaRecorder.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data)
            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' })
                const audioFile = new File([audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' })
                stream.getTracks().forEach(t => t.stop())
                onRecordingComplete(audioFile)
            }
            mediaRecorder.start()
            setRecording(true)
            timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000)
        } catch {
            onCancel()
        }
    }

    const stopRecording = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop()
            if (timerRef.current) clearInterval(timerRef.current)
            setRecording(false)
        }
    }

    const cancelRecording = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop())
            mediaRecorderRef.current.stop()
        }
        if (timerRef.current) clearInterval(timerRef.current)
        onCancel()
    }

    const fmtTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

    return (
        <Fade in>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                mb: 1, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fee2e2',
            }}>
                <Box sx={{
                    width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444',
                    animation: 'pulse-rec 1.4s infinite',
                    '@keyframes pulse-rec': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.5, transform: 'scale(0.85)' },
                    },
                }} />
                <Typography variant="body2" fontWeight={600} color="error.main" sx={{ flex: 1 }}>
                    {fmtTime(recordingTime)}
                </Typography>
                <IconButton size="small" onClick={cancelRecording}
                    sx={{ color: 'grey.500', '&:hover': { color: 'error.main' } }}>
                    <Stop fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={stopRecording}
                    sx={{ bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                    <Send fontSize="small" />
                </IconButton>
            </Box>
        </Fade>
    )
}

// ────────────────────────────────────────────────
//  Typing Dots Animation
// ────────────────────────────────────────────────
const TypingDots = () => (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 0.5, px: 0.5 }}>
        {[0, 1, 2].map(i => (
            <Box key={i} sx={{
                width: 7, height: 7, borderRadius: '50%', bgcolor: 'grey.400',
                animation: 'typing-bounce 1.4s infinite ease-in-out',
                animationDelay: `${i * 0.16}s`,
                '@keyframes typing-bounce': {
                    '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                    '40%': { transform: 'scale(1)', opacity: 1 },
                },
            }} />
        ))}
    </Box>
)

// ────────────────────────────────────────────────
//  Message Content
// ────────────────────────────────────────────────
const MessageContent = ({ msg }) => {
    const isVisitor = msg.sender_type === 'visitor'
    const fileUrl = msg.file_url ? `${FILE_BASE}${msg.file_url}` : null
    const type = msg.message_type || 'text'

    if (type === 'image' && fileUrl) {
        return (
            <Box>
                <Box
                    component="img"
                    src={fileUrl}
                    alt={msg.file_name || 'Image'}
                    sx={{
                        maxWidth: '100%',
                        maxHeight: 220,
                        borderRadius: 2,
                        cursor: 'pointer',
                        display: 'block',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.02)' },
                    }}
                    onClick={() => window.open(fileUrl, '_blank')}
                />
                {msg.message && msg.message !== msg.file_name && msg.message !== 'Voice message' && (
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', opacity: 0.85 }}>
                        {msg.message}
                    </Typography>
                )}
            </Box>
        )
    }

    if (type === 'voice' && fileUrl) {
        return <CustomAudioPlayer src={fileUrl} isVisitor={isVisitor} />
    }

    if (type === 'file' && fileUrl) {
        return (
            <Box
                component="a"
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: isVisitor ? '#fff' : BRAND.primary,
                    textDecoration: 'none',
                    p: 0.8,
                    borderRadius: 1.5,
                    bgcolor: isVisitor ? 'rgba(255,255,255,0.12)' : `${BRAND.primary}08`,
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: isVisitor ? 'rgba(255,255,255,0.2)' : `${BRAND.primary}12` },
                }}
            >
                <InsertDriveFile fontSize="small" />
                <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: 500 }}>
                    {msg.file_name || msg.message || 'Download file'}
                </Typography>
            </Box>
        )
    }

    return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {msg.message}
        </Typography>
    )
}

// ────────────────────────────────────────────────
//  Date Separator Component
// ────────────────────────────────────────────────
const DateSeparator = ({ date, isArabic }) => {
    const today = new Date()
    const msgDate = new Date(date)
    const isToday = today.toDateString() === msgDate.toDateString()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const isYesterday = yesterday.toDateString() === msgDate.toDateString()

    let label
    if (isToday) label = isArabic ? 'اليوم' : 'Today'
    else if (isYesterday) label = isArabic ? 'أمس' : 'Yesterday'
    else label = msgDate.toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric' })

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2, my: 1.5, px: 2,
        }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(0,0,0,0.07)' }} />
            <Typography variant="caption" sx={{
                color: 'text.disabled', fontSize: 11, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.5,
                bgcolor: '#f8f9fb', px: 1.5, py: 0.3, borderRadius: 10,
                border: '1px solid rgba(0,0,0,0.06)',
            }}>
                {label}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(0,0,0,0.07)' }} />
        </Box>
    )
}

// ────────────────────────────────────────────────
//  MAIN COMPONENT
// ────────────────────────────────────────────────
const PublicChat = () => {
    const { formId } = useParams()
    const [config, setConfig] = useState(null)

    // Detect widget mode and language from URL params
    const urlParams = useMemo(() => new URLSearchParams(window.location.search), [])
    const isWidget = urlParams.get('widget') === '1'
    const urlLang = urlParams.get('lang') || ''
    const defaultLang = urlLang ? urlLang : (navigator.language?.startsWith('ar') ? 'ar' : 'en')
    const [selectedLang, setSelectedLang] = useState(defaultLang)
    const isArabic = selectedLang === 'ar'

    // Derive brand colors from config (dynamic from admin settings)
    const brand = useMemo(() => getBrand(config), [config])
    const logoSrc = useMemo(() => {
        const url = config?.logo_url
        if (!url) return '/moj-logo.png'
        if (url.startsWith('http')) return url
        return `${FILE_BASE}${url}`
    }, [config])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [conversation, setConversation] = useState(null)
    const [messages, setMessages] = useState([])
    const [newMessage, setNewMessage] = useState('')
    const [sending, setSending] = useState(false)
    const [visitorName, setVisitorName] = useState('')
    const [visitorEmail, setVisitorEmail] = useState('')
    const [started, setStarted] = useState(false)
    const [showRecorder, setShowRecorder] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [showWelcome, setShowWelcome] = useState(false)
    const [soundEnabled, setSoundEnabled] = useState(true)
    const [unreadCount, setUnreadCount] = useState(0)
    const [showScrollBtn, setShowScrollBtn] = useState(false)
    const [newMsgPill, setNewMsgPill] = useState(null) // { text: '...' } or null
    const messagesEndRef = useRef(null)
    const messagesContainerRef = useRef(null)
    const visitorId = useRef(getVisitorId())
    const fileInputRef = useRef(null)
    const imageInputRef = useRef(null)
    const isNearBottomRef = useRef(true)
    const originalTitleRef = useRef(document.title)
    const titleFlashRef = useRef(null)

    // ── Scroll Helpers ──
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        setUnreadCount(0)
        setNewMsgPill(null)
    }, [])

    const checkIfNearBottom = useCallback(() => {
        const el = messagesContainerRef.current
        if (!el) return true
        const threshold = 120
        return el.scrollHeight - el.scrollTop - el.clientHeight < threshold
    }, [])

    const handleMessagesScroll = useCallback(() => {
        const nearBottom = checkIfNearBottom()
        isNearBottomRef.current = nearBottom
        setShowScrollBtn(!nearBottom)
        if (nearBottom) {
            setUnreadCount(0)
            setNewMsgPill(null)
        }
    }, [checkIfNearBottom])

    // ── Title flash for new messages ──
    const startTitleFlash = useCallback((preview) => {
        if (titleFlashRef.current) clearInterval(titleFlashRef.current)
        let on = true
        titleFlashRef.current = setInterval(() => {
            document.title = on ? `💬 ${preview}` : originalTitleRef.current
            on = !on
        }, 1200)
    }, [])

    const stopTitleFlash = useCallback(() => {
        if (titleFlashRef.current) {
            clearInterval(titleFlashRef.current)
            titleFlashRef.current = null
        }
        document.title = originalTitleRef.current
    }, [])

    // Stop flashing when tab gains focus
    useEffect(() => {
        const handleFocus = () => stopTitleFlash()
        window.addEventListener('focus', handleFocus)
        return () => {
            window.removeEventListener('focus', handleFocus)
            stopTitleFlash()
        }
    }, [stopTitleFlash])

    // ── Browser Notification ──
    const showBrowserNotification = useCallback((title, body) => {
        if (document.hasFocus()) return
        if (Notification.permission === 'granted') {
            try {
                new Notification(title, {
                    body,
                    icon: '/moj-logo.png',
                    tag: 'fms-chat',
                    renotify: true,
                })
            } catch { /* mobile fallback */ }
        }
    }, [])

    // Request notification permission when chat starts
    const requestNotifPermission = useCallback(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission()
        }
    }, [])

    // Load config
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const res = await chatPublicApi.getConfig(formId)
                setConfig(res.data)
                setLoading(false)
                setTimeout(() => setShowWelcome(true), 300)
            } catch {
                setError('Chat is not available for this form.')
                setLoading(false)
            }
        }
        loadConfig()
    }, [formId])

    // Start / resume conversation
    const startChat = async () => {
        try {
            const res = await chatPublicApi.startConversation({
                form_id: parseInt(formId),
                visitor_identifier: visitorId.current,
                visitor_name: visitorName || 'Visitor',
                visitor_email: visitorEmail || undefined,
                lang: selectedLang,
            })
            setConversation(res.data)
            setStarted(true)
            requestNotifPermission()
            // Backend sends welcome message automatically based on lang
            await fetchMessages(res.data.id)
        } catch {
            setError('Failed to start chat.')
        }
    }

    // Track last known message count for new-message detection
    const lastMsgCountRef = useRef(0)

    // Full-replace message list
    const fetchMessages = useCallback(async (convId) => {
        try {
            const cid = convId || conversation?.id
            if (!cid) return
            const res = await chatPublicApi.getMessages(cid, visitorId.current)
            const serverMsgs = res.data?.messages || res.messages || []
            if (serverMsgs.length > 0) {
                setMessages(serverMsgs)

                // Detect new agent messages
                if (serverMsgs.length > lastMsgCountRef.current && lastMsgCountRef.current > 0) {
                    const newMsgs = serverMsgs.slice(lastMsgCountRef.current)
                    const agentMsgs = newMsgs.filter(m => m.sender_type === 'agent')
                    if (agentMsgs.length > 0) {
                        const latest = agentMsgs[agentMsgs.length - 1]
                        const preview = latest.message_type === 'text'
                            ? (latest.message || '').substring(0, 120)
                            : (isArabic ? '📎 مرفق جديد' : '📎 New attachment')
                        const senderName = latest.sender_name || (isArabic ? 'الدعم' : 'Support')

                        // 🔔 Sound notification
                        if (soundEnabled) playNotificationSound()

                        // 🔔 Browser notification (when tab not focused)
                        showBrowserNotification(senderName, preview)

                        // 🔔 Title flash (when tab not focused)
                        if (!document.hasFocus()) {
                            startTitleFlash(isArabic ? 'رسالة جديدة' : 'New message')
                        }

                        // If user scrolled up, show pill + increment unread
                        if (!isNearBottomRef.current) {
                            setUnreadCount(prev => prev + agentMsgs.length)
                            setNewMsgPill({
                                text: isArabic ? 'رسالة جديدة ⬇' : '⬇ New message',
                            })
                        } else {
                            setTimeout(scrollToBottom, 100)
                        }

                        // Notify parent widget
                        try {
                            window.parent.postMessage({
                                type: 'fms-chat-new-message',
                                count: agentMsgs.length,
                                preview: preview,
                                senderName: senderName,
                            }, '*')
                        } catch { /* cross-origin silent */ }
                    }
                } else {
                    // First load – just scroll
                    setTimeout(scrollToBottom, 100)
                }
                lastMsgCountRef.current = serverMsgs.length
            }
        } catch { /* silent */ }
    }, [conversation?.id, scrollToBottom, isArabic, soundEnabled, showBrowserNotification, startTitleFlash])

    // Polling
    useEffect(() => {
        if (!started || !conversation?.id) return
        const poll = setInterval(() => fetchMessages(), POLL_INTERVAL)
        return () => clearInterval(poll)
    }, [started, conversation?.id, fetchMessages])

    // Send text
    const handleSend = async () => {
        if (!newMessage.trim() || sending) return
        const msgText = newMessage.trim()
        setSending(true)
        setNewMessage('')
        try {
            await chatPublicApi.sendMessage(conversation.id, visitorId.current, msgText)
            await fetchMessages()
        } catch {
            setNewMessage(msgText)
        } finally {
            setSending(false)
        }
    }

    // File / image upload
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file || !conversation?.id) return
        e.target.value = ''
        setUploading(true)
        try {
            await chatPublicApi.uploadFile(conversation.id, visitorId.current, file)
            await fetchMessages()
        } catch (err) {
            console.error('Upload error:', err)
        } finally {
            setUploading(false)
        }
    }

    // Voice recording complete
    const handleRecordingComplete = async (audioFile) => {
        setShowRecorder(false)
        setUploading(true)
        try {
            await chatPublicApi.uploadFile(conversation.id, visitorId.current, audioFile, 'voice')
            await fetchMessages()
        } catch (err) {
            console.error('Voice upload error:', err)
        } finally {
            setUploading(false)
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    // ── Helpers: Date separator logic ──
    const shouldShowDateSep = (msgs, idx) => {
        if (idx === 0) return true
        const curr = new Date(msgs[idx].created_at).toDateString()
        const prev = new Date(msgs[idx - 1].created_at).toDateString()
        return curr !== prev
    }

    // ── Loading ──
    if (loading) {
        return (
            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                minHeight: '100vh', background: brand.gradient,
            }}>
                <Fade in timeout={800}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress sx={{ color: '#fff' }} size={48} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 2 }}>Loading chat...</Typography>
                    </Box>
                </Fade>
            </Box>
        )
    }

    // ── Error ──
    if (error) {
        return (
            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            }}>
                <Grow in timeout={600}>
                    <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400, borderRadius: 4, boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
                        <ChatIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
                        <Typography variant="h6" color="error" fontWeight={600}>{error}</Typography>
                    </Paper>
                </Grow>
            </Box>
        )
    }

    return (
        <Box sx={{
            minHeight: isWidget ? '100%' : '100vh',
            background: isWidget ? 'transparent' : brand.gradientLight,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: isWidget ? 0 : 2,
            height: isWidget ? '100%' : 'auto',
        }}>
            <Grow in timeout={500}>
                <Paper
                    elevation={isWidget ? 0 : 0}
                    sx={{
                        width: '100%',
                        maxWidth: isWidget ? '100%' : 440,
                        height: isWidget ? '100%' : '88vh',
                        maxHeight: isWidget ? '100%' : 720,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: isWidget ? 0 : 4,
                        overflow: 'hidden',
                        boxShadow: isWidget ? 'none' : '0 12px 48px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                        border: isWidget ? 'none' : '1px solid rgba(0,0,0,0.06)',
                    }}
                >
                    {/* ── HEADER (dynamic from settings) ── */}
                    <Box sx={{
                        background: brand.headerGradient,
                        color: '#fff',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::after': {
                            content: '""', position: 'absolute',
                            top: -30, right: -30, width: 100, height: 100,
                            borderRadius: '50%', background: 'rgba(255,255,255,0.07)',
                        },
                        '&::before': {
                            content: '""', position: 'absolute',
                            bottom: -15, left: -15, width: 50, height: 50,
                            borderRadius: '50%', background: 'rgba(255,255,255,0.05)',
                        },
                    }}>
                        {/* Logo (from settings or default) */}
                        <Box sx={{
                            width: 46,
                            height: 46,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.95)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}>
                            <Box
                                component="img"
                                src={logoSrc}
                                alt="Logo"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    objectFit: 'contain',
                                }}
                            />
                        </Box>
                        <Box sx={{ zIndex: 1, flex: 1 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: '1.05rem' }}>
                                {isArabic
                                    ? (config?.header_title_ar || config?.header_title || config?.form_title_ar || 'محادثة مباشرة')
                                    : (config?.header_title_en || config?.header_title || config?.form_title || 'Live Chat')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                <Box sx={{
                                    width: 7, height: 7, borderRadius: '50%',
                                    bgcolor: started ? '#4caf50' : 'rgba(255,255,255,0.5)',
                                    boxShadow: started ? '0 0 6px #4caf50' : 'none',
                                    transition: 'all 0.3s',
                                }} />
                                <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
                                    {started ? (isArabic ? 'متصل' : 'Connected') : (isArabic
                                        ? (config?.header_subtitle_ar || config?.header_subtitle || 'ابدأ محادثة')
                                        : (config?.header_subtitle_en || config?.header_subtitle || 'Start a conversation'))}
                                </Typography>
                            </Box>
                        </Box>
                        {/* Sound toggle (only when chatting) */}
                        {started && (
                            <Tooltip title={soundEnabled ? (isArabic ? 'كتم الصوت' : 'Mute') : (isArabic ? 'تفعيل الصوت' : 'Unmute')}>
                                <IconButton
                                    size="small"
                                    onClick={() => setSoundEnabled(p => !p)}
                                    sx={{ color: 'rgba(255,255,255,0.75)', zIndex: 1, '&:hover': { color: '#fff' } }}
                                >
                                    {soundEnabled ? <VolumeUp sx={{ fontSize: 18 }} /> : <VolumeOff sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {!started ? (
                        /* ── PRE-CHAT FORM ── */
                        <Fade in={showWelcome} timeout={600}>
                            <Box sx={{
                                flex: 1, display: 'flex', flexDirection: 'column',
                                justifyContent: 'center', alignItems: 'center',
                                p: 4,
                                background: 'linear-gradient(180deg, #fff 0%, #f4f7f9 100%)',
                            }}>
                                {/* Logo circle */}
                                <Box sx={{
                                    width: 80, height: 80, borderRadius: '50%',
                                    bgcolor: `${brand.primary}0D`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    mb: 2.5,
                                    boxShadow: `0 0 0 10px ${brand.primary}08`,
                                }}>
                                    <Box
                                        component="img"
                                        src={logoSrc}
                                        alt="Logo"
                                        sx={{ width: 44, height: 44, objectFit: 'contain' }}
                                    />
                                </Box>

                                {/* Language toggle */}
                                <Box sx={{ display: 'flex', gap: 0.5, mb: 2, bgcolor: '#f0f2f5', borderRadius: 6, p: 0.5 }}>
                                    <Box
                                        component="button"
                                        onClick={() => setSelectedLang('ar')}
                                        sx={{
                                            px: 2.5, py: 0.7,
                                            border: 'none',
                                            borderRadius: 5,
                                            bgcolor: selectedLang === 'ar' ? '#fff' : 'transparent',
                                            color: selectedLang === 'ar' ? brand.primary : '#888',
                                            fontWeight: selectedLang === 'ar' ? 700 : 500,
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            transition: 'all 0.25s',
                                            fontFamily: 'inherit',
                                            boxShadow: selectedLang === 'ar' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                        }}
                                    >
                                        العربية
                                    </Box>
                                    <Box
                                        component="button"
                                        onClick={() => setSelectedLang('en')}
                                        sx={{
                                            px: 2.5, py: 0.7,
                                            border: 'none',
                                            borderRadius: 5,
                                            bgcolor: selectedLang === 'en' ? '#fff' : 'transparent',
                                            color: selectedLang === 'en' ? brand.primary : '#888',
                                            fontWeight: selectedLang === 'en' ? 700 : 500,
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            transition: 'all 0.25s',
                                            fontFamily: 'inherit',
                                            boxShadow: selectedLang === 'en' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                        }}
                                    >
                                        English
                                    </Box>
                                </Box>

                                {/* Short greeting */}
                                <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                                    {isArabic ? 'مرحباً بكم 👋' : 'Welcome 👋'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 280, lineHeight: 1.6 }}>
                                    {isArabic
                                        ? 'أدخل اسمك لبدء المحادثة مع فريقنا'
                                        : 'Enter your name to start chatting with our team'}
                                </Typography>

                                {/* Name input */}
                                <Box sx={{ width: '100%', maxWidth: 320 }}>
                                    <TextField
                                        fullWidth
                                        label={isArabic ? 'اسمك' : 'Your Name'}
                                        value={visitorName}
                                        onChange={(e) => setVisitorName(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && visitorName.trim() && startChat()}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2.5,
                                                transition: 'box-shadow 0.3s',
                                                '&.Mui-focused': { boxShadow: `0 0 0 3px ${brand.primary}18` },
                                            },
                                        }}
                                        size="small"
                                        inputProps={{ dir: isArabic ? 'rtl' : 'ltr' }}
                                    />

                                    {config?.require_email && (
                                        <TextField
                                            fullWidth label={isArabic ? 'البريد الإلكتروني' : 'Email'} type="email"
                                            value={visitorEmail}
                                            onChange={(e) => setVisitorEmail(e.target.value)}
                                            sx={{
                                                mb: 2,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2.5,
                                                    transition: 'box-shadow 0.3s',
                                                    '&.Mui-focused': { boxShadow: `0 0 0 3px ${brand.primary}18` },
                                                },
                                            }}
                                            size="small" required
                                        />
                                    )}

                                    <Box
                                        component="button"
                                        onClick={startChat}
                                        disabled={!visitorName.trim()}
                                        sx={{
                                            width: '100%',
                                            mt: 0.5, py: 1.5, px: 4,
                                            background: visitorName.trim() ? brand.gradient : '#ccc',
                                            color: '#fff', border: 'none', borderRadius: 2.5,
                                            fontSize: 15, fontWeight: 600,
                                            cursor: visitorName.trim() ? 'pointer' : 'not-allowed',
                                            transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                                            boxShadow: visitorName.trim() ? `0 4px 14px ${brand.primary}40` : 'none',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                                            '&:hover': visitorName.trim() ? {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 6px 20px ${brand.primary}50`,
                                            } : {},
                                            '&:active': { transform: 'translateY(0)' },
                                        }}
                                    >
                                        <ChatIcon sx={{ fontSize: 18 }} />
                                        {isArabic ? 'ابدأ المحادثة' : 'Start Chat'}
                                    </Box>
                                </Box>
                            </Box>
                        </Fade>
                    ) : (
                        <>
                            {/* ── MESSAGES ── */}
                            <Box
                                ref={messagesContainerRef}
                                onScroll={handleMessagesScroll}
                                sx={{
                                    flex: 1, overflowY: 'auto', p: 2,
                                    display: 'flex', flexDirection: 'column', gap: 0.8,
                                    bgcolor: '#f8f9fb',
                                    scrollBehavior: 'smooth',
                                    position: 'relative',
                                    '&::-webkit-scrollbar': { width: 5 },
                                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.12)', borderRadius: 10 },
                                }}
                            >
                                {messages.map((msg, idx) => {
                                    const isVisitor = msg.sender_type === 'visitor'
                                    const showDateSep = shouldShowDateSep(messages, idx)
                                    const showSenderName = !isVisitor && (idx === 0 || messages[idx - 1]?.sender_type === 'visitor')
                                    return (
                                        <Box key={msg.id || idx}>
                                            {showDateSep && <DateSeparator date={msg.created_at} isArabic={isArabic} />}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: isVisitor ? 'flex-end' : 'flex-start',
                                                    animation: 'msg-slide-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                    '@keyframes msg-slide-in': {
                                                        from: { opacity: 0, transform: `translateY(12px) scale(0.96)` },
                                                        to: { opacity: 1, transform: 'translateY(0) scale(1)' },
                                                    },
                                                }}
                                            >
                                                {/* Agent sender name */}
                                                {showSenderName && (
                                                    <Typography variant="caption" sx={{
                                                        ml: 1.5, mb: 0.3, fontWeight: 600,
                                                        color: brand.primary, fontSize: 11,
                                                        letterSpacing: 0.2,
                                                    }}>
                                                        {msg.sender_name || (isArabic ? 'فريق الدعم' : 'Support Team')}
                                                    </Typography>
                                                )}
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        px: 2, py: 1.2, maxWidth: '80%',
                                                        borderRadius: isVisitor ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                        bgcolor: isVisitor ? brand.primary : '#fff',
                                                        color: isVisitor ? '#fff' : 'text.primary',
                                                        boxShadow: isVisitor
                                                            ? `0 2px 12px ${brand.primary}30`
                                                            : '0 1px 4px rgba(0,0,0,0.06)',
                                                        transition: 'transform 0.15s',
                                                        '&:hover': { transform: 'scale(1.01)' },
                                                    }}
                                                >
                                                    <MessageContent msg={msg} />
                                                    <Box sx={{
                                                        display: 'flex', alignItems: 'center',
                                                        justifyContent: 'flex-end', gap: 0.5, mt: 0.5,
                                                    }}>
                                                        <Typography variant="caption" sx={{
                                                            opacity: 0.55, fontSize: 10, fontWeight: 500,
                                                        }}>
                                                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </Typography>
                                                        {/* Read receipt for visitor messages */}
                                                        {isVisitor && (
                                                            <Typography variant="caption" sx={{
                                                                fontSize: 12, opacity: 0.7, lineHeight: 1,
                                                            }}>
                                                                ✓✓
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        </Box>
                                    )
                                })}

                                {sending && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <Paper elevation={0} sx={{
                                            px: 2, py: 1, borderRadius: '18px 18px 18px 4px',
                                            bgcolor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                        }}>
                                            <TypingDots />
                                        </Paper>
                                    </Box>
                                )}

                                <div ref={messagesEndRef} />

                                {/* ── Scroll to bottom FAB ── */}
                                <Fade in={showScrollBtn}>
                                    <Box
                                        onClick={scrollToBottom}
                                        sx={{
                                            position: 'sticky', bottom: 8,
                                            alignSelf: 'center',
                                            display: 'flex', alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            bgcolor: '#fff',
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            borderRadius: 20,
                                            px: 1.5, py: 0.6,
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                            transition: 'all 0.2s',
                                            zIndex: 10,
                                            '&:hover': {
                                                boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        <KeyboardArrowDown sx={{ fontSize: 18, color: brand.primary }} />
                                        {unreadCount > 0 && (
                                            <Typography variant="caption" sx={{
                                                fontWeight: 700, color: '#fff',
                                                bgcolor: brand.primary,
                                                borderRadius: 10, px: 0.8, py: 0.1,
                                                fontSize: 11, lineHeight: '16px',
                                                minWidth: 18, textAlign: 'center',
                                            }}>
                                                {unreadCount}
                                            </Typography>
                                        )}
                                    </Box>
                                </Fade>

                                {/* ── New message pill ── */}
                                {newMsgPill && !showScrollBtn && (
                                    <Slide direction="up" in mountOnEnter unmountOnExit>
                                        <Box
                                            onClick={scrollToBottom}
                                            sx={{
                                                position: 'sticky', bottom: 8,
                                                alignSelf: 'center',
                                                cursor: 'pointer',
                                                bgcolor: brand.primary,
                                                color: '#fff',
                                                borderRadius: 20,
                                                px: 2, py: 0.7,
                                                fontSize: 13, fontWeight: 600,
                                                boxShadow: `0 4px 16px ${brand.primary}40`,
                                                animation: 'pill-bounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                '@keyframes pill-bounce': {
                                                    from: { transform: 'translateY(20px) scale(0.8)', opacity: 0 },
                                                    to: { transform: 'translateY(0) scale(1)', opacity: 1 },
                                                },
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                },
                                            }}
                                        >
                                            {newMsgPill.text}
                                        </Box>
                                    </Slide>
                                )}
                            </Box>

                            {/* ── INPUT AREA ── */}
                            <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff' }}>
                                <input type="file" ref={fileInputRef} style={{ display: 'none' }}
                                    accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.txt" onChange={handleFileUpload} />
                                <input type="file" ref={imageInputRef} style={{ display: 'none' }}
                                    accept="image/*" onChange={handleFileUpload} />

                                {/* Upload progress */}
                                {uploading && (
                                    <Fade in>
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 1,
                                            mb: 1, px: 1.5, py: 0.8, bgcolor: `${brand.primary}08`, borderRadius: 2,
                                        }}>
                                            <CircularProgress size={14} sx={{ color: brand.primary }} />
                                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                {isArabic ? 'جاري الرفع...' : 'Uploading...'}
                                            </Typography>
                                        </Box>
                                    </Fade>
                                )}

                                {/* Voice Recorder */}
                                {showRecorder && (
                                    <VoiceRecorder
                                        onRecordingComplete={handleRecordingComplete}
                                        onCancel={() => setShowRecorder(false)}
                                    />
                                )}

                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                                    <Tooltip title={isArabic ? 'إرسال صورة' : 'Send photo'} arrow>
                                        <IconButton size="small"
                                            onClick={() => imageInputRef.current?.click()}
                                            disabled={uploading || showRecorder}
                                            sx={{
                                                color: 'grey.500', transition: 'all 0.2s',
                                                '&:hover': { color: brand.primary, bgcolor: `${brand.primary}10` },
                                            }}
                                        >
                                            <ImageIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={isArabic ? 'إرسال ملف' : 'Send file'} arrow>
                                        <IconButton size="small"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading || showRecorder}
                                            sx={{
                                                color: 'grey.500', transition: 'all 0.2s',
                                                '&:hover': { color: brand.primary, bgcolor: `${brand.primary}10` },
                                            }}
                                        >
                                            <AttachFile fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={showRecorder ? (isArabic ? 'جاري التسجيل...' : 'Recording...') : (isArabic ? 'رسالة صوتية' : 'Voice message')} arrow>
                                        <IconButton size="small"
                                            onClick={() => !showRecorder && setShowRecorder(true)}
                                            disabled={uploading || showRecorder}
                                            sx={{
                                                color: showRecorder ? '#ef4444' : 'grey.500',
                                                transition: 'all 0.2s',
                                                '&:hover': { color: brand.primary, bgcolor: `${brand.primary}10` },
                                            }}
                                        >
                                            <Mic fontSize="small" />
                                        </IconButton>
                                    </Tooltip>

                                    <TextField
                                        fullWidth
                                        placeholder={isArabic ? 'اكتب رسالة...' : 'Type a message...'}
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        multiline maxRows={3} size="small" variant="outlined"
                                        disabled={showRecorder}
                                        inputProps={{ dir: isArabic ? 'rtl' : 'ltr' }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 3, bgcolor: '#f5f6f8',
                                                transition: 'all 0.2s',
                                                '& fieldset': { borderColor: 'transparent' },
                                                '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
                                                '&.Mui-focused fieldset': { borderColor: brand.primary, borderWidth: 1.5 },
                                                '&.Mui-focused': { bgcolor: '#fff' },
                                            },
                                        }}
                                    />
                                    <IconButton
                                        onClick={handleSend}
                                        disabled={!newMessage.trim() || sending || showRecorder}
                                        sx={{
                                            background: brand.gradient,
                                            color: '#fff', width: 42, height: 42,
                                            transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                                            boxShadow: `0 2px 8px ${brand.primary}40`,
                                            '&:hover': {
                                                background: brand.gradient,
                                                transform: 'scale(1.08)',
                                                boxShadow: `0 4px 14px ${brand.primary}50`,
                                            },
                                            '&.Mui-disabled': {
                                                background: 'grey.200', bgcolor: '#e2e8f0',
                                                color: 'grey.400', boxShadow: 'none',
                                            },
                                        }}
                                    >
                                        <Send sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        </>
                    )}
                </Paper>
            </Grow>
        </Box >
    )
}

export default PublicChat
